// HTTP Client Wrapper with Authentication and Retry Logic
import { KeycloakService } from './keycloak.service';

interface RequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  retries?: number;
}

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

/** Structured API error carrying backend error details */
export class ApiError extends Error {
  status: number;
  errorCode: string | null;
  validationError: string | null;
  details: any;

  constructor(
    status: number,
    message: string,
    errorCode?: string,
    validationError?: string,
    details?: any
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode || null;
    this.validationError = validationError || null;
    this.details = details || null;
  }
}

class HttpClient {
  private defaultTimeout = 30000; // 30 seconds
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
  };

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoff(attempt: number): number {
    const delay = this.retryConfig.baseDelay * Math.pow(2, attempt);
    return Math.min(delay, this.retryConfig.maxDelay);
  }

  /**
   * Check if error is retryable (5xx errors)
   */
  private isRetryableError(status: number): boolean {
    return status >= 500 && status < 600;
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Make HTTP request with timeout, auth headers, and retry logic
   */
  private async request<T>(
    url: string,
    config: RequestConfig
  ): Promise<T> {
    const timeout = config.timeout || this.defaultTimeout;
    const maxRetries = config.retries ?? this.retryConfig.maxRetries;

    let lastError: Error | null = null;
    let retried401 = false;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
          // Get fresh auth token (async — refreshes if about to expire)
          const token = await KeycloakService.getValidToken();
          if (!token && KeycloakService.isAuthenticated()) {
            console.warn('[httpClient] Token refresh failed — request will go unauthenticated:', url);
          }
          const authHeaders: Record<string, string> = token
            ? { Authorization: `Bearer ${token}` }
            : {};

          // Prepare headers
          const headers: Record<string, string> = {
            ...config.headers,
            ...authHeaders,
          };

          // Add Content-Type for JSON bodies
          if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
          }

          // Make request
          const response = await fetch(url, {
            method: config.method,
            headers,
            body: config.body instanceof FormData
              ? config.body
              : config.body
                ? JSON.stringify(config.body)
                : undefined,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          // Handle non-OK responses
          if (!response.ok) {
            // 401 Unauthorized — force token refresh and retry once
            if (response.status === 401 && !retried401) {
              retried401 = true;
              const refreshed = await KeycloakService.refreshToken();
              if (refreshed) {
                attempt--; // don't consume a retry attempt for auth recovery
                continue;
              }
            }

            // Check if error is retryable
            if (this.isRetryableError(response.status) && attempt < maxRetries) {
              const delay = this.calculateBackoff(attempt);
              console.warn(`Request failed with status ${response.status}. Retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
              await this.sleep(delay);
              continue;
            }

            // Non-retryable error or max retries reached
            const errorData = await response.json().catch(() => ({}));
            const detail = errorData.error || errorData.detail || errorData.message || response.statusText;
            throw new ApiError(
              response.status,
              detail,
              errorData.error_code,
              errorData.details?.validation_error,
              errorData.details
            );
          }

          // Parse response
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await response.json();
          }

          // Return empty object for non-JSON responses
          return {} as T;

        } finally {
          clearTimeout(timeoutId);
        }

      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Handle abort/timeout errors
        if (lastError.name === 'AbortError') {
          lastError = new Error(`Request timeout after ${timeout}ms`);
          break;
        }

        // HTTP errors (4xx, 5xx already handled above) — don't retry
        if (lastError instanceof ApiError || lastError.message.startsWith('HTTP ')) {
          break;
        }

        // Retry only on network errors (fetch failed, no response)
        if (attempt < maxRetries) {
          const delay = this.calculateBackoff(attempt);
          console.warn(`Network error: ${lastError.message}. Retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          await this.sleep(delay);
          continue;
        }

        break;
      }
    }

    // If we got here, all retries failed
    throw lastError || new Error('Request failed after all retries');
  }

  /**
   * GET request
   */
  async get<T>(url: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(url, {
      method: 'GET',
      ...config,
    });
  }

  /**
   * POST request
   */
  async post<T>(url: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      body,
      ...config,
    });
  }

  /**
   * PUT request
   */
  async put<T>(url: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      body,
      ...config,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(url, {
      method: 'DELETE',
      ...config,
    });
  }
}

// Export singleton instance
export const httpClient = new HttpClient();
